import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import LocationPickerMap from '../Components/LocationPickerMap/LocationPickerMap';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getCartSessionId } from '../lib/cartSession';
import { checkoutCart, getAccount, validatePromoCode } from '../lib/api';
import { formatNpr } from '../lib/currency';
import { reverseGeocodeOpenStreetMap, searchOpenStreetMap } from '../lib/openStreetMap';

const defaultKathmanduLocation = {
  lat: '27.7172',
  lon: '85.3240',
  label: 'Kathmandu, Bagmati Province, Nepal',
};

function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, clearAll, openCart } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('Kathmandu');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(defaultKathmanduLocation);
  const [promoCode, setPromoCode] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [form, setForm] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: '',
    address: '',
    notes: '',
  });

  const deliveryFee = useMemo(() => (cart.subtotal >= 100 ? 0 : 5.99), [cart.subtotal]);
  const discountAmount = useMemo(() => Number(appliedPromo?.discountAmount || 0), [appliedPromo]);
  const grandTotal = useMemo(
    () => Number((cart.subtotal + deliveryFee - discountAmount).toFixed(2)),
    [cart.subtotal, deliveryFee, discountAmount]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadSavedCheckoutData() {
      if (!user?.id) {
        return;
      }

      try {
        const response = await getAccount({ userId: user.id });
        if (!isMounted) {
          return;
        }

        const savedAddress = response.user.addressBook?.[0] || {};
        setForm((current) => ({
          ...current,
          fullName: response.user.fullName || current.fullName,
          email: response.user.email || current.email,
          phone: response.user.phone || savedAddress.phone || current.phone,
          address: savedAddress.address || savedAddress.fullAddress || savedAddress.addressLine1 || current.address,
          notes: savedAddress.deliveryNotes || current.notes,
        }));

        if (savedAddress.latitude && savedAddress.longitude) {
          setSelectedLocation({
            lat: savedAddress.latitude,
            lon: savedAddress.longitude,
            label:
              savedAddress.locationLabel ||
              savedAddress.address ||
              savedAddress.fullAddress ||
              savedAddress.addressLine1,
          });
          setSearchQuery(
            savedAddress.locationLabel ||
              savedAddress.address ||
              savedAddress.fullAddress ||
              savedAddress.addressLine1
          );
        }
      } catch (_error) {
        // Checkout can still proceed with manual entry.
      }
    }

    void loadSavedCheckoutData();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  if (!cart.items.length) {
    return <Navigate to="/cart" replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await checkoutCart({
        sessionId: getCartSessionId(),
        userId: user?.id || '',
        paymentMethod: 'cash_on_delivery',
        customer: {
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
        },
        shippingAddress: {
          fullAddress: form.address,
          locationLabel: selectedLocation?.label || '',
          latitude: selectedLocation?.lat || '',
          longitude: selectedLocation?.lon || '',
        },
        notes: form.notes,
        promoCode: appliedPromo?.promoCode?.code || '',
      });

      await clearAll().catch(() => {});
      navigate('/orders', {
        replace: true,
        state: {
          placedOrder: response.order,
        },
      });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleApplyPromoCode() {
    const trimmedCode = promoCode.trim();

    if (!trimmedCode) {
      setAppliedPromo(null);
      setError('Enter a promo code first.');
      return;
    }

    setIsApplyingPromo(true);
    setError('');

    try {
      const response = await validatePromoCode({
        sessionId: getCartSessionId(),
        userId: user?.id || '',
        code: trimmedCode,
      });

      setAppliedPromo(response);
      setPromoCode(response.promoCode.code);
    } catch (promoError) {
      setAppliedPromo(null);
      setError(promoError.message);
    } finally {
      setIsApplyingPromo(false);
    }
  }

  function handleRemovePromoCode() {
    setAppliedPromo(null);
    setPromoCode('');
    setError('');
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleAddressSearch(event) {
    event.preventDefault();

    const query = searchQuery.trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    try {
      const results = await searchOpenStreetMap(query);
      setSearchResults(results);

      if (results.length > 0) {
        applySearchResult(results[0]);
      } else {
        setSelectedLocation(null);
      }
    } catch (searchError) {
      setError(searchError.message);
    } finally {
      setIsSearching(false);
    }
  }

  function applySearchResult(result) {
    const address = result.address || {};
    const line1 = [
      address.house_number,
      address.road || address.pedestrian || address.footway,
    ]
      .filter(Boolean)
      .join(' ');

    setSelectedLocation({
      lat: result.lat,
      lon: result.lon,
      label: result.display_name,
    });
    setForm((current) => ({
      ...current,
      address: result.display_name || line1 || current.address,
    }));
    setSearchResults([]);
    setSearchQuery(result.display_name);
  }

  async function handleMapPickLocation(nextLocation) {
    setSelectedLocation({
      ...nextLocation,
      label: 'Selected map location',
    });
    setError('');

    try {
      const result = await reverseGeocodeOpenStreetMap(nextLocation.lat, nextLocation.lon);
      const displayName = result.display_name || `Pinned location (${Number(nextLocation.lat).toFixed(5)}, ${Number(nextLocation.lon).toFixed(5)})`;

      setSelectedLocation({
        lat: nextLocation.lat,
        lon: nextLocation.lon,
        label: displayName,
      });
      updateField('address', displayName);
      setSearchQuery(displayName);
      setSearchResults([]);
    } catch (mapError) {
      setError(mapError.message);
    }
  }

  return (
    <section className="page-card checkout-page">
      <div className="checkout-page__hero">
        <div className="checkout-page__hero-copy">
          <span className="checkout-page__eyebrow">Secure Checkout</span>
          <h1 className="page-card__title">Checkout</h1>
          <p className="page-card__text checkout-page__intro">
            Keep checkout simple with your basic details, one address field, delivery notes, and map location.
          </p>
        </div>
        <div className="checkout-page__hero-meta">
          <span className="page-chip">Cash on delivery</span>
          <span className="page-chip">Saved details when logged in</span>
          <span className="page-chip">Live order summary</span>
        </div>
      </div>

      {!user ? (
        <div className="orders-account-prompt orders-account-prompt--checkout">
          <div className="orders-account-prompt__copy">
            <span className="checkout-page__eyebrow">Use Your Account</span>
            <strong>Create account to track your order</strong>
            <p>Save your details for faster checkout next time.</p>
          </div>
          <div className="orders-account-prompt__actions">
            <Link
              className="green-button orders-account-prompt__primary"
              to="/login"
              state={{ from: '/checkout' }}
            >
              Login
            </Link>
            <Link className="product-card__link" to="/signup" state={{ from: '/checkout' }}>
              Create Account
            </Link>
          </div>
        </div>
      ) : (
        <div className="checkout-page__account-note">
          <span className="checkout-page__eyebrow">Account Ready</span>
          <p>Your saved contact details are already filled in for faster checkout.</p>
        </div>
      )}

      <div className="page-chip-row page-chip-row--left checkout-page__support-row">
        <span className="page-chip">Map-based address search</span>
        <span className="page-chip">Clean delivery form</span>
        <span className="page-chip">Always-visible payment summary</span>
      </div>
      <div className="checkout-layout">
        <form className="auth-form checkout-form" onSubmit={handleSubmit}>
          <div className="checkout-location checkout-form__full">
            <div className="checkout-location__header">
              <div>
                <span className="checkout-location__label">Address Search</span>
                <h2 className="checkout-location__title">Find address on map</h2>
              </div>
            </div>
            <div className="checkout-location__search">
              <input
                type="search"
                placeholder="Search area, street, or landmark"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                }}
              />
              <button
                type="button"
                className="product-card__link"
                onClick={handleAddressSearch}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchResults.length ? (
              <div className="checkout-location__results">
                {searchResults.map((result) => (
                  <button
                    key={`${result.place_id}`}
                    type="button"
                    className="checkout-location__result"
                    onClick={() => {
                      applySearchResult(result);
                    }}
                  >
                    {result.display_name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="checkout-location__map-shell">
              <LocationPickerMap
                selectedLocation={selectedLocation}
                onPickLocation={(nextLocation) => {
                  void handleMapPickLocation(nextLocation);
                }}
              />
            </div>
            {selectedLocation ? (
              <p className="checkout-location__selected">
                Selected location: {selectedLocation.label}. Click anywhere on the map to move the pin.
              </p>
            ) : null}
          </div>
          <label className="auth-form__field">
            <span>Full Name</span>
            <input
              required
              type="text"
              value={form.fullName}
              onChange={(event) => updateField('fullName', event.target.value)}
            />
          </label>
          <label className="auth-form__field">
            <span>Email</span>
            <input
              required
              type="email"
              value={form.email}
              onChange={(event) => updateField('email', event.target.value)}
            />
          </label>
          <label className="auth-form__field">
            <span>Phone</span>
            <input
              required
              type="tel"
              value={form.phone}
              onChange={(event) => updateField('phone', event.target.value)}
            />
          </label>
          <label className="auth-form__field checkout-form__full">
            <span>Delivery Notes</span>
            <textarea
              rows="4"
              value={form.notes}
              onChange={(event) => updateField('notes', event.target.value)}
            />
          </label>
          <div className="checkout-promo checkout-form__full">
            <div className="checkout-promo__header">
              <span className="checkout-location__label">Promo Code</span>
              {appliedPromo ? <strong>{appliedPromo.promoCode.percentOff}% off applied</strong> : null}
            </div>
            <div className="checkout-promo__controls">
              <input
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(event) => {
                  setPromoCode(event.target.value.toUpperCase());
                }}
              />
              <button
                type="button"
                className="product-card__link"
                onClick={() => {
                  void handleApplyPromoCode();
                }}
              >
                {isApplyingPromo ? 'Applying...' : 'Apply'}
              </button>
              {appliedPromo ? (
                <button
                  type="button"
                  className="cart-item__remove"
                  onClick={handleRemovePromoCode}
                >
                  Remove
                </button>
              ) : null}
            </div>
          </div>
          {error ? <p className="products-state products-state--error">{error}</p> : null}
          <div className="checkout-form__actions">
            <button
              type="button"
              className="product-card__link checkout-back-link checkout-back-link--button"
              onClick={openCart}
            >
              Back to Cart
            </button>
            <GreenButton
              disabled={isSubmitting}
              type="submit"
              text={isSubmitting ? 'Placing Order...' : 'Place COD Order'}
            />
          </div>
        </form>

        <aside className="cart-summary checkout-summary">
          <span className="checkout-page__eyebrow">Payment</span>
          <h2>Cash on Delivery</h2>
          <p className="checkout-summary__text">Pay after your order arrives at your address.</p>
          <div className="checkout-summary__divider" />
          <div className="checkout-summary__section">
            <div className="checkout-summary__heading-row">
              <h3>Order Summary</h3>
              <span>{cart.itemCount} item{cart.itemCount === 1 ? '' : 's'}</span>
            </div>
            <div className="checkout-summary__line">
              <span>Subtotal</span>
              <strong>{formatNpr(cart.subtotal)}</strong>
            </div>
            <div className="checkout-summary__line">
              <span>Delivery</span>
              <strong>{formatNpr(deliveryFee)}</strong>
            </div>
          {appliedPromo ? (
              <div className="checkout-summary__line checkout-summary__line--discount">
                <span>Discount ({appliedPromo.promoCode.code})</span>
                <strong>-{formatNpr(discountAmount)}</strong>
              </div>
          ) : null}
            <div className="checkout-summary__divider" />
            <div className="checkout-summary__line checkout-summary__line--total">
              <span>Total</span>
              <strong>{formatNpr(grandTotal)}</strong>
            </div>
          </div>
          <p className="checkout-summary__note">
            Delivery notes and address details from the form will be used for this order.
          </p>
        </aside>
      </div>
    </section>
  );
}

export default CheckoutPage;
